/**
 * The rep dashboard widgets from processflow.docx Step 2 — today's activities,
 * the pipeline strip, this month's sales, the agenda, and the "recent …" lists.
 *
 * Every widget reads one field of `GET /dashboards/rep` and renders nothing when
 * that field is absent, so the sample-data fallback (which predates these
 * fields) still draws the page it always drew.
 *
 * Orders are deliberately not shown: there is no order table behind them yet
 * (`pipelineCounts` ends at Won). When one exists it arrives as one more entry
 * in that array and renders here with no change.
 */
import { Link } from 'react-router-dom';
import { KIND_META, KIND_LABEL } from '../activities/ActivityTimeline.jsx';
import { Card, Badge, Avatar, Meter, EmptyState } from '../../components/common/index.jsx';
import { money, moneyShort, number, time, date, relativeTime } from '../../utils/formatters.js';

/** "Good morning" / "afternoon" / "evening" — the doc's greeting follows the clock. */
export function greeting(now = new Date()) {
  const h = now.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

const kindIcon = (kind) => (KIND_META[kind] ?? ['bi-circle', 'var(--ink-3)']);

/** A small icon-in-circle used by every list row here, tinted by activity kind. */
function KindDot({ kind }) {
  const [icon, color] = kindIcon(kind);
  return (
    <span className="tl-dot flex-shrink-0" style={{ background: color, position: 'static', width: 27, height: 27 }}>
      <i className={`bi ${icon}`} aria-hidden="true" />
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Today                                                                       */
/* -------------------------------------------------------------------------- */

const TODAY_TILES = [
  { key: 'calls', label: 'Calls', icon: 'bi-telephone-fill', to: '/activities?kind=call' },
  { key: 'meetings', label: 'Meetings', icon: 'bi-people-fill', to: '/activities?kind=meeting' },
  { key: 'visits', label: 'Visits', icon: 'bi-geo-alt-fill', to: '/activities?kind=visit' },
  { key: 'followUps', label: 'Follow-ups', icon: 'bi-arrow-repeat', to: '/activities?view=followups' },
];

export function TodayActivitiesCard({ today, className = '' }) {
  if (!today) return null;
  const total = TODAY_TILES.reduce((s, t) => s + (today[t.key] ?? 0), 0);

  return (
    <Card className={className} title="Today's activities" subtitle={total ? `${total} planned for today` : 'Nothing planned yet'}>
      <div className="row g-2 text-center">
        {TODAY_TILES.map((t) => (
          <div className="col-6" key={t.key}>
            <Link
              to={t.to}
              className="d-block p-3 text-decoration-none"
              style={{ background: 'var(--surface-2)', border: '1px solid var(--line-2)', borderRadius: 'var(--r)' }}
            >
              <div className="t-2xl fw-750 lh-1 tabular ink">{today[t.key] ?? 0}</div>
              <div className="t-2xs ink-3 mt-1"><i className={`bi ${t.icon} me-1`} aria-hidden="true" />{t.label}</div>
            </Link>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Pipeline strip                                                              */
/* -------------------------------------------------------------------------- */

const STAGE_ROUTE = {
  newLeads: '/leads', qualified: '/leads', opportunities: '/opportunities',
  quotations: '/quotations', won: '/opportunities',
};

/** Lead → Qualified → Opportunity → Quotation → Won, as the doc draws it. */
export function PipelineFlowCard({ stages, className = '' }) {
  if (!stages?.length) return null;

  return (
    <Card className={className} title="Sales pipeline" subtitle="Lead → Qualified → Opportunity → Quotation → Won">
      <div className="d-flex flex-column gap-2">
        {stages.map((s, i) => (
          <Link
            key={s.key}
            to={STAGE_ROUTE[s.key] ?? '/opportunities'}
            className="d-flex align-items-center gap-2 text-decoration-none"
          >
            <span className="t-2xs ink-4 tabular" style={{ width: 14 }}>{i + 1}</span>
            <span className="t-sm ink-2 flex-grow-1">{s.label}</span>
            <span className="fw-750 tabular ink">{number(s.value)}</span>
          </Link>
        ))}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* My sales                                                                    */
/* -------------------------------------------------------------------------- */

export function MySalesCard({ sales, conversion, expected, className = '' }) {
  if (!sales) return null;
  const monthName = new Date().toLocaleDateString('en-GB', { month: 'long' });

  return (
    <Card className={className} title="My sales" subtitle={monthName}>
      <div className="d-flex justify-content-between align-items-baseline mb-1">
        <span className="t-xs ink-3">This month</span>
        <span className="t-lg fw-750 tabular">{money(sales.thisMonth, sales.currency)}</span>
      </div>
      {sales.target != null ? (
        <>
          <div className="d-flex justify-content-between t-xs mb-1">
            <span className="ink-3">
              Target{sales.targetBasis === 'prorated' && <span className="ink-4"> (spread evenly)</span>}
            </span>
            <span className="fw-650 tabular">{money(sales.target, sales.currency)}</span>
          </div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <Meter value={sales.pct ?? 0} tone="mint" />
            <Badge tone={sales.pct >= 90 ? 'emerald' : sales.pct >= 60 ? 'amber' : 'rose'}>{sales.pct}%</Badge>
          </div>
          <div className="t-2xs ink-4">Achievement</div>
        </>
      ) : (
        <div className="t-xs ink-3">No revenue target is set for you this period.</div>
      )}

      {(conversion || expected) && <hr className="soft" />}
      <div className="row g-2 text-center t-xs">
        {conversion && (
          <>
            <div className="col-6">
              <div className="fw-750 t-md tabular">{conversion.leadToCustomerPct}%</div>
              <div className="ink-3">Lead → customer</div>
            </div>
            <div className="col-6">
              <div className="fw-750 t-md tabular">{conversion.winRatePct}%</div>
              <div className="ink-3">Win rate</div>
            </div>
          </>
        )}
        {expected && (
          <>
            <div className="col-6 mt-2">
              <div className="fw-750 t-md tabular">{moneyShort(expected.thisMonth, sales.currency)}</div>
              <div className="ink-3">Expected this month</div>
            </div>
            <div className="col-6 mt-2">
              <div className="fw-750 t-md tabular">{moneyShort(expected.thisQuarter, sales.currency)}</div>
              <div className="ink-3">Expected this quarter</div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Activity lists                                                              */
/* -------------------------------------------------------------------------- */

/** Today's agenda in time order — the doc's "10:00 Call – ABC Ltd" list. */
export function UpcomingTodayCard({ items, className = '' }) {
  if (!items) return null;

  return (
    <Card
      className={className}
      title="Upcoming activities"
      subtitle="Today, in time order"
      actions={<Link to="/activities?view=calendar" className="btn btn-soft btn-sm-x">Calendar</Link>}
    >
      {items.length === 0 ? (
        <div className="t-xs ink-3 text-center py-2">Nothing else scheduled today.</div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {items.map((a) => (
            <div key={a.id} className="d-flex align-items-center gap-2">
              <span className="t-xs fw-650 tabular ink-2" style={{ width: 44 }}>{time(a.at)}</span>
              <KindDot kind={a.kind} />
              <div className="min-w-0 flex-grow-1">
                <div className="t-sm fw-600 text-truncate">{KIND_LABEL[a.kind] ?? a.kind} — {a.related ?? a.subject}</div>
                <div className="t-2xs ink-4 text-truncate">{a.related ? a.subject : ''}{a.location ? ` · ${a.location}` : ''}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function OverdueActivitiesCard({ overdue, className = '' }) {
  if (!overdue) return null;

  return (
    <Card
      className={className}
      title="Overdue activities"
      actions={<Badge tone={overdue.total ? 'rose' : 'emerald'}>{overdue.total}</Badge>}
    >
      {overdue.items.length === 0 ? (
        <div className="t-xs ink-3 text-center py-2">Nothing overdue.</div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {overdue.items.map((a) => (
            <div key={a.id} className="d-flex align-items-center gap-2">
              <KindDot kind={a.kind} />
              <div className="min-w-0 flex-grow-1">
                <div className="t-sm fw-600 text-truncate">{a.subject}</div>
                <div className="t-2xs ink-4 text-truncate">{a.related ?? 'No related record'}</div>
              </div>
              <Badge tone="rose" className="no-wrap">{a.daysOverdue}d</Badge>
            </div>
          ))}
          {overdue.total > overdue.items.length && (
            <Link to="/activities?view=followups" className="t-xs fw-650">
              View all {overdue.total} <i className="bi bi-arrow-right" aria-hidden="true" />
            </Link>
          )}
        </div>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Records                                                                     */
/* -------------------------------------------------------------------------- */

export function NewLeadsCard({ leads, className = '' }) {
  if (!leads) return null;

  return (
    <Card
      className={className}
      title="New leads"
      subtitle="Assigned to me in the last 7 days"
      actions={<Link to="/leads" className="t-xs fw-650">All leads</Link>}
    >
      {leads.length === 0 ? (
        <div className="t-xs ink-3 text-center py-2">No new leads this week.</div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {leads.map((l) => (
            <div key={l.id} className="d-flex align-items-center gap-2">
              <Avatar name={l.name ?? l.company ?? '?'} size={27} />
              <div className="min-w-0 flex-grow-1">
                <Link to={`/leads/${l.id}`} className="t-sm fw-600 d-block text-truncate">{l.name ?? l.company}</Link>
                <div className="t-2xs ink-4 text-truncate">
                  {[l.company, l.source].filter(Boolean).join(' · ')} · {relativeTime(l.createdAt)}
                </div>
              </div>
              {l.status && <Badge tone="brand">{l.status}</Badge>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function MyOpportunitiesCard({ opps, className = '' }) {
  if (!opps) return null;

  return (
    <Card
      className={className}
      title="My opportunities"
      subtitle="Open deals, soonest close first"
      actions={<Link to="/opportunities" className="btn btn-soft btn-sm-x">All opportunities</Link>}
      bare
      footer={opps.total > opps.items.length && (
        <span className="t-xs ink-3">Showing {opps.items.length} of {opps.total}</span>
      )}
    >
      {opps.items.length === 0 ? (
        <div className="card-body-x">
          <EmptyState icon="bi-bullseye" title="No open opportunities" message="Qualify a lead to create one." />
        </div>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Opportunity</th><th>Stage</th><th className="num">Value</th>
                <th className="num">Prob.</th><th>Close</th>
              </tr>
            </thead>
            <tbody>
              {opps.items.map((o) => (
                <tr key={o.id}>
                  <td>
                    <div className="strong text-truncate">{o.name}</div>
                    <div className="t-2xs ink-4 text-truncate">{o.account}</div>
                  </td>
                  <td><Badge tone="sky">{o.stage}</Badge></td>
                  <td className="num strong">{moneyShort(o.value)}</td>
                  <td className="num">{o.probability != null ? `${o.probability}%` : '—'}</td>
                  <td className="t-xs no-wrap">{date(o.closeDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

const QUOTE_TONE = {
  draft: 'neutral', pending_approval: 'amber', approved: 'emerald', rejected: 'rose', sent: 'sky',
  viewed: 'violet', accepted: 'emerald', declined: 'rose', expired: 'neutral', revised: 'neutral',
};

export function RecentQuotationsCard({ quotes, className = '' }) {
  if (!quotes) return null;

  return (
    <Card
      className={className}
      title="Recent quotations"
      actions={<Link to="/quotations" className="btn btn-soft btn-sm-x">All quotations</Link>}
      bare
    >
      {quotes.length === 0 ? (
        <div className="card-body-x">
          <EmptyState icon="bi-file-earmark-text" title="No quotations yet" message="Quotes you create appear here." />
        </div>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr><th>Quote</th><th>Customer</th><th>Status</th><th className="num">Total</th></tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.id}>
                  <td>
                    <div className="strong">{q.quoteNo}</div>
                    <div className="t-2xs ink-4">{date(q.quoteDate)}</div>
                  </td>
                  <td>{q.account ?? '—'}</td>
                  <td>
                    <Badge tone={QUOTE_TONE[q.status] ?? 'neutral'}>{q.status.replace(/_/g, ' ')}</Badge>
                  </td>
                  <td className="num strong">{moneyShort(q.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export function PendingApprovalsCard({ approvals, className = '' }) {
  if (!approvals) return null;

  return (
    <Card
      className={className}
      title="Pending approvals"
      subtitle="My quotations waiting on a decision"
      actions={<Badge tone={approvals.total ? 'amber' : 'emerald'}>{approvals.total}</Badge>}
    >
      {approvals.items.length === 0 ? (
        <div className="t-xs ink-3 text-center py-2">None of your quotations are waiting.</div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {approvals.items.map((a) => (
            <div key={a.id} className="d-flex align-items-center gap-2">
              <div className="min-w-0 flex-grow-1">
                <div className="t-sm fw-600 text-truncate">{a.quoteNo} · {a.account ?? '—'}</div>
                <div className="t-2xs ink-4">{moneyShort(a.total)} · waiting {a.waitedHours}h</div>
              </div>
              <Link to="/quotations" className="btn btn-soft btn-sm-x no-wrap">Open</Link>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/** Customers = accounts I own with at least one won opportunity. */
export function RecentCustomersCard({ customers, className = '' }) {
  if (!customers) return null;

  return (
    <Card
      className={className}
      title="Recent customers"
      subtitle="Accounts with a won deal"
      actions={<Link to="/accounts" className="t-xs fw-650">All accounts</Link>}
    >
      {customers.length === 0 ? (
        <div className="t-xs ink-3 text-center py-2">No won deals yet.</div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {customers.map((c) => (
            <div key={c.id} className="d-flex align-items-center gap-2">
              <Avatar name={c.name} size={27} />
              <div className="min-w-0 flex-grow-1">
                <Link to={`/accounts/${c.id}`} className="t-sm fw-600 d-block text-truncate">{c.name}</Link>
                <div className="t-2xs ink-4">
                  {c.deals} won deal{c.deals === 1 ? '' : 's'} · last {date(c.lastWonOn)}
                </div>
              </div>
              <span className="t-xs fw-750 tabular">{moneyShort(c.wonValue)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/** The doc's "end of day" tally. Orders join it when an order table exists. */
const SUMMARY_ROWS = [
  ['calls', 'Calls'], ['visits', 'Visits'], ['meetings', 'Meetings'],
  ['newLeads', 'New leads'], ['opportunities', 'Opportunities'], ['quotes', 'Quotes'],
];

export function DailySummaryCard({ summary, className = '' }) {
  if (!summary) return null;

  return (
    <Card className={className} title="Daily activity summary" subtitle="Everything logged today">
      <div className="d-flex flex-column gap-1">
        {SUMMARY_ROWS.map(([key, label]) => (
          <div key={key} className="d-flex justify-content-between t-sm">
            <span className="ink-3">{label}</span>
            <span className="fw-750 tabular">{summary[key] ?? 0}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}
